/**
 * Enchanting table book (vanilla EnchantingTableBlockEntity + EnchantTableRenderer
 * + BookModel on entity/enchantment/enchanting_table_book.png, 64x32): floats
 * 0.75 above the table, bobs, turns toward the nearest player within 3 blocks,
 * opens when one is near and flips pages at random. Tables are tracked through
 * block-change events (see blockChanged), never by scanning.
 */

import * as THREE from 'three';
import { boxGeometry } from './firstPerson';

interface BookState {
  key: string; x: number; y: number; z: number;
  time: number; flip: number; oFlip: number; flipT: number; flipA: number;
  open: number; oOpen: number; rot: number; oRot: number; tRot: number;
  group: THREE.Group; parts: Record<string, THREE.Object3D>;
}

const RANGE = 3;

export class EnchantBookRenderer {
  private readonly root = new THREE.Group();
  private readonly mat: THREE.MeshBasicMaterial;
  private readonly books = new Map<string, BookState>();

  constructor(scene: THREE.Scene) {
    const tex = new THREE.TextureLoader().load('mc/entity/enchantment/enchanting_table_book.png');
    tex.magFilter = tex.minFilter = THREE.NearestFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.generateMipmaps = false;
    this.mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide, alphaTest: 0.1 });
    scene.add(this.root);
  }

  private build(): { group: THREE.Group; parts: Record<string, THREE.Object3D> } {
    const g = new THREE.Group();
    const box = (u: number, v: number, x: number, y: number, z: number, dx: number, dy: number, dz: number): THREE.Mesh =>
      new THREE.Mesh(boxGeometry(u, v, x, y, z, dx, dy, dz, 0, 64, 32), this.mat);
    const pivot = (mesh: THREE.Mesh, ox: number, oy: number, oz: number): THREE.Group => {
      const p = new THREE.Group(); p.position.set(ox, oy, oz); p.add(mesh); g.add(p); return p;
    };
    // BookModel.createBodyLayer (px, y down)
    const parts: Record<string, THREE.Object3D> = {
      leftLid: pivot(box(0, 0, -6, -5, -0.005, 6, 10, 0.005), 0, 0, -1),
      rightLid: pivot(box(16, 0, 0, -5, -0.005, 6, 10, 0.005), 0, 0, 1),
      seam: pivot(box(12, 0, -1, -5, 0, 2, 10, 0.005), 0, 0, 0),
      leftPages: pivot(box(0, 10, 0, -4, -0.99, 5, 8, 1), 0, 0, 0),
      rightPages: pivot(box(12, 10, 0, -4, -0.01, 5, 8, 1), 0, 0, 0),
      flip1: pivot(box(24, 10, 0, -4, 0, 5, 8, 0.005), 0, 0, 0),
      flip2: pivot(box(24, 10, 0, -4, 0, 5, 8, 0.005), 0, 0, 0),
    };
    parts.seam.rotation.y = Math.PI / 2;
    // px -> blocks, model y-down -> y-up
    g.scale.set(1 / 16, -1 / 16, 1 / 16);
    const outer = new THREE.Group();
    outer.add(g);
    return { group: outer, parts };
  }

  /**
   * Table bookkeeping from ChunkManager.onBlockChange: every table is a player edit
   * (worldgen places none), and saved edits are re-reported when their chunk loads
   * (wakeEdits), so this sees every table without scanning.
   */
  blockChanged(x: number, y: number, z: number, isTable: boolean): void {
    const key = `${x},${y},${z}`;
    const have = this.books.get(key);
    if (isTable && !have) {
      const b = this.build();
      this.root.add(b.group);
      this.books.set(key, { key, x, y, z, time: Math.random() * 1000, flip: 0, oFlip: 0, flipT: 0, flipA: 0,
        open: 0, oOpen: 0, rot: 0, oRot: 0, tRot: Math.random() * Math.PI * 2, group: b.group, parts: b.parts });
    } else if (!isTable && have) {
      this.root.remove(have.group);
      this.books.delete(key);
    }
  }

  /** 20 Hz book animation; books more than 64 blocks away are hidden and not ticked. */
  tick(px: number, py: number, pz: number): void {
    for (const b of this.books.values()) {
      b.group.visible = Math.abs(b.x - px) < 64 && Math.abs(b.z - pz) < 64;
      if (!b.group.visible) continue;
      // EnchantingTableBlockEntity.bookAnimationTick
      b.oOpen = b.open; b.oRot = b.rot;
      const dx = px - (b.x + 0.5), dz = pz - (b.z + 0.5);
      const near = dx * dx + dz * dz + (py - b.y) ** 2 < RANGE * RANGE;
      if (near) {
        b.tRot = Math.atan2(dz, dx);
        b.open += 0.1;
        if (b.open < 0.5 || Math.random() * 40 < 1) {
          const f1 = b.flipT;
          do b.flipT += Math.floor(Math.random() * 4) - Math.floor(Math.random() * 4);
          while (f1 === b.flipT);
        }
      } else {
        b.tRot += 0.02;
        b.open -= 0.1;
      }
      while (b.rot >= Math.PI) b.rot -= Math.PI * 2;
      while (b.rot < -Math.PI) b.rot += Math.PI * 2;
      while (b.tRot >= Math.PI) b.tRot -= Math.PI * 2;
      while (b.tRot < -Math.PI) b.tRot += Math.PI * 2;
      let d = b.tRot - b.rot;
      while (d >= Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      b.rot += d * 0.4;
      b.open = Math.max(0, Math.min(1, b.open));
      b.time++;
      b.oFlip = b.flip;
      let f = (b.flipT - b.flip) * 0.4;
      f = Math.max(-0.2, Math.min(0.2, f));
      b.flipA += (f - b.flipA) * 0.9;
      b.flip += b.flipA;
    }
  }

  /** Pose every book for this frame (EnchantTableRenderer.render). */
  update(alpha: number, light: (x: number, y: number, z: number) => number): void {
    for (const b of this.books.values()) {
      const t = b.time + alpha;
      const g = b.group;
      g.position.set(b.x + 0.5, b.y + 0.75 + 0.1 + Math.sin(t * 0.1) * 0.01, b.z + 0.5);
      let dr = b.rot - b.oRot;
      while (dr >= Math.PI) dr -= Math.PI * 2;
      while (dr < -Math.PI) dr += Math.PI * 2;
      const rot = b.oRot + dr * alpha;
      g.rotation.set(0, 0, 0);
      g.rotation.order = 'YXZ';
      g.rotation.y = -rot;
      g.rotation.z = (80 * Math.PI) / 180;
      const flip = b.oFlip + (b.flip - b.oFlip) * alpha;
      const f6 = Math.max(0, Math.min(1, (flip + 0.25 - Math.floor(flip + 0.25)) * 1.6 - 0.3));
      const f7 = Math.max(0, Math.min(1, (flip + 0.75 - Math.floor(flip + 0.75)) * 1.6 - 0.3));
      const open = b.oOpen + (b.open - b.oOpen) * alpha;
      // BookModel.setupAnim
      const a = (Math.sin(t * 0.02) * 0.1 + 1.25) * open;
      const p = b.parts;
      p.leftLid.rotation.y = Math.PI + a;
      p.rightLid.rotation.y = -a;
      p.leftPages.rotation.y = a;
      p.rightPages.rotation.y = -a;
      p.flip1.rotation.y = a - a * 2 * f6;
      p.flip2.rotation.y = a - a * 2 * f7;
      const sx = Math.sin(a);
      p.leftPages.position.x = sx; p.rightPages.position.x = sx; p.flip1.position.x = sx; p.flip2.position.x = sx;
      const l = 0.3 + 0.7 * Math.max(0, Math.min(1, light(b.x + 0.5, b.y + 1, b.z + 0.5)));
      this.mat.color.setRGB(l, l, l);
    }
  }
}
